import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { Box, Button, Typography, Snackbar, Link, CircularProgress } from '@mui/material'
import { useQuery, useMutation } from '@tanstack/react-query'

import CampaignToolbar from '../CampaignToolbar'
import {
  fetchCampaign,
  deleteFlight,
  deleteCampaign,
  updateCampaignStatus,
  hubspotDealRequiredOwnerIds,
  gtvOwnerId
} from '../getData'
import {
  getTotalBudget,
  getTargetImpressions,
  getCPM,
  getPlayFrequency,
  formatDate
} from '../utils'
import { CampaignStatus, DeliveryType, ErrorResponse } from '../../types/campaign'
import FlightList from '../icons/FlightList'
import StackedSectionLayout from '../StackedSectionLayout'
import InfoDisplay from '../InfoDisplay'
import FlightAccordion from '../FlightAccordion'
import CircularLoader from '../CircularLoader'
import StatusChip from '../StatusChip'
import OwnerIcon from '../icons/OwnerIcon'
import AdvertiserIcon from '../icons/AdvertiserIcon'
import TimerIcon from '../icons/TimerIcon'
import ImpressionsIcon from '../icons/ImpressionsIcon'
import DollarBill from '../icons/DollarBill'
import CalendarStart from '../icons/CalendarStart'
import CalendarEnd from '../icons/CalendarEnd'
import StatusIcon from '../icons/StatusIcon'
import ReportIcon from '../icons/ReportIcon'
import ContentCategory from '../icons/ContentCategory'
import CampaignsPlaceholder from '../icons/CampaignsPlaceholder'
import AlertMessage, { AlertContents } from '../AlertMessage'
import ConfirmationModal from '../ConfirmationModal'
import theme from '../theme'
import { UserAuthResponse } from '../../types/campaign'

interface ModalState {
  open: boolean
  id: number
  type: 'campaign' | 'flight'
}

interface Props {
  user: UserAuthResponse | null
}

const CampaignDetails = ({ user }: Props) => {
  const isGtvUser = user ? user?.company.id === gtvOwnerId : false
  const navigate = useNavigate()
  const { id } = useParams()
  const [alertContents, setAlertContents] = useState<AlertContents | null>(null)
  const [openSnackbar, setOpenSnackbar] = useState<{
    open: boolean
    message: string
  }>({ open: false, message: '' })
  const [openModal, setOpenModal] = useState<ModalState>({
    open: false,
    id: 0,
    type: 'flight'
  })

  const {
    data: campaign,
    isLoading: isLoadingCampaign,
    refetch: refetchCampaign
  } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => fetchCampaign(Number(id)),
    refetchOnMount: true
  })

  const { mutate: updateCampaignStatusMutation, isPending: isPendingStatusUpdate } = useMutation({
    mutationFn: (status: string) => updateCampaignStatus(Number(id), status),
    onSuccess: () => {
      refetchCampaign()
      setAlertContents({
        open: true,
        message: 'Campaign status updated successfully',
        severity: 'success'
      })
    },
    onError: (error: AxiosError) => {
      console.error(error)
      const errorData = error.response?.data as ErrorResponse
      setAlertContents({
        open: true,
        message: errorData.context.join('<br />') || 'Failed to update campaign status',
        severity: 'error'
      })
    }
  })

  const { mutate: deleteCampaignMutation, isPending: isPendingDeleteCampaign } = useMutation({
    mutationFn: () => deleteCampaign(Number(id)),
    onSuccess: () => {
      setAlertContents({
        open: true,
        message: 'Campaign deleted successfully',
        severity: 'success'
      })
      navigate('/')
    },
    onError: (error: AxiosError) => {
      console.error(error)
      const errorData = error.response?.data as ErrorResponse
      setAlertContents({
        open: true,
        message: errorData.context?.[0] || 'Error deleting campaign',
        severity: 'error'
      })
    }
  })

  const { mutate: deleteFlightMutation, isPending: isPendingDeleteFlight } = useMutation({
    mutationFn: (flightId: number) => deleteFlight(flightId, Number(id)),
    onSuccess: () => {
      refetchCampaign()
      setOpenSnackbar({
        open: true,
        message: 'Flight deleted successfully'
      })
      setOpenModal({ open: false, id: 0, type: 'flight' })
    },
    onError: () => {
      setOpenSnackbar({
        open: true,
        message: 'Failed to delete flight'
      })
    }
  })

  if (!campaign || isLoadingCampaign || isPendingDeleteCampaign) {
    return <CircularLoader />
  }

  if (campaign.status === CampaignStatus.Deleted) {
    return (
      <Box data-testid="campaign-details">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.space['40'],
            backgroundColor: theme.palette.background.paper,
            padding: theme.space['80'],
            margin: theme.space['40'],
            borderRadius: theme.borders['rounded-10'],
            color: theme.palette['sage-400']
          }}
        >
          <CampaignsPlaceholder sx={{ fontSize: '20rem' }} />
          <Typography
            variant="heading-2"
            sx={{ color: theme.palette['sage-400'], textAlign: 'center' }}
          >
            This campaign has been deleted.
            <br />
            Return to the <Link href="/">campaigns list</Link>.
          </Typography>
        </Box>
      </Box>
    )
  }

  const handleEdit = () => {
    navigate(`/campaign/${campaign.id}/edit`, {
      state: campaign
    })
  }

  const handleAddFlight = () => {
    navigate(`/campaign/${campaign.id}/flight/create`, {
      state: campaign
    })
  }

  const openDeleteFlightModal = (flightId: number) => {
    setOpenModal({ open: true, id: flightId, type: 'flight' })
  }

  const openDeleteCampaignModal = () => {
    setOpenModal({ open: true, id: campaign.id, type: 'campaign' })
  }

  const campaignHasStarted = campaign?.flights.some((flight) =>
    dayjs(flight.duration.starts_at).tz(flight.time_zone).isBefore(dayjs())
  )

  const campaignStartTime = `${formatDate(
    campaign.calculated_duration.starts_at,
    campaign.flights[0]?.time_zone
  )}`
  const campaignEndTime = `${formatDate(
    campaign.calculated_duration.ends_at,
    campaign.flights[0]?.time_zone
  )}`

  const flightsHeader = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: theme.space['40'],
        marginBottom: theme.space['40'],
        gap: theme.space['8']
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center'
        }}
      >
        <FlightList sx={{ marginRight: theme.space['20'], width: '28px', height: '28px' }} />
        <Typography variant="heading-8">Flights</Typography>
      </Box>
      <Button
        variant="contained"
        color="primary"
        onClick={handleAddFlight}
        sx={{
          height: '56px',
          width: '169px'
        }}
      >
        <Typography variant="ui-3">New Flight</Typography>
      </Button>
    </Box>
  )

  const campaignMetrics = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        rowGap: theme.space['40'],
        columnGap: theme.space['96']
      }}
    >
      {hubspotDealRequiredOwnerIds.includes(campaign.owner.id) && (
        <InfoDisplay
          label="Hubspot Deal Id"
          value={campaign.hubspot_deal_id}
          data-testid="hubspot-display"
          flex={0}
        />
      )}
      <InfoDisplay
        label="Owner"
        value={campaign.owner.name}
        icon={<OwnerIcon sx={{ width: '17px', height: '22px' }} />}
        flex={0}
      />
      <InfoDisplay
        label="Advertiser"
        value={campaign.advertiser.name}
        icon={<AdvertiserIcon sx={{ width: '17px', height: '22px' }} />}
        flex={0}
      />
      <InfoDisplay
        label="Status"
        value={
          isPendingStatusUpdate ? (
            <CircularProgress
              sx={{
                color: theme.palette['accent-500']
              }}
              size={22}
            />
          ) : (
            <StatusChip status={campaign.status} onStatusChange={updateCampaignStatusMutation} />
          )
        }
        icon={<StatusIcon sx={{ width: '17px', height: '22px' }} />}
        flex={0}
      />
      {campaign.content_category && (
        <InfoDisplay
          label="Content Category"
          value={campaign.content_category.name}
          icon={<ContentCategory />}
          flex={0}
        />
      )}
      <InfoDisplay
        label="Budget"
        value={getTotalBudget(campaign)}
        icon={<DollarBill sx={{ width: '17px', height: '20px' }} />}
        flex={0}
      />
      {campaign.delivery_type === DeliveryType.Impressions && (
        <InfoDisplay
          label="Impressions"
          value={getTargetImpressions(campaign)}
          icon={<ImpressionsIcon sx={{ width: '17px', height: '22px' }} />}
          flex={0}
        />
      )}
      {campaign.delivery_type === DeliveryType.PlayFrequency && (
        <InfoDisplay
          label="Play Frequency"
          value={getPlayFrequency(campaign)}
          icon={<ImpressionsIcon sx={{ width: '17px', height: '22px' }} />}
          flex={0}
        />
      )}
      {campaign.delivery_type === DeliveryType.Impressions && (
        <InfoDisplay
          label="CPM"
          value={getCPM(campaign)}
          icon={<TimerIcon sx={{ width: '17px', height: '22px' }} />}
          flex={0}
        />
      )}
      <InfoDisplay
        label="Start Date"
        value={campaignStartTime}
        icon={<CalendarStart sx={{ width: '17px', height: '19px' }} />}
        flex={0}
      />
      <InfoDisplay
        label="End Date"
        value={campaignEndTime}
        icon={<CalendarEnd sx={{ width: '17px', height: '19px' }} />}
        flex={0}
      />
      {isGtvUser && (
        <InfoDisplay
          label="Campaign Report"
          value={
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                gap: theme.space['8']
              }}
            >
              <Link
                href={`https://grocerytv.omniapp.co/dashboards/2da7aa69?f--k079S-Qq=%7B%22values%22%3A%5B%22${campaign.id}%22%5D%7D`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: 'underline',
                  color: theme.palette['base-black']
                }}
              >
                View
              </Link>
            </Box>
          }
          icon={<ReportIcon sx={{ width: '17px', height: '19px' }} />}
          flex={0}
        />
      )}
    </Box>
  )

  return (
    <Box data-testid="campaign-details">
      {alertContents?.open && (
        <AlertMessage alert={alertContents} onClose={() => setAlertContents(null)} />
      )}
      <Snackbar
        open={openSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({ open: false, message: '' })}
        message={openSnackbar.message}
      />
      <ConfirmationModal
        open={openModal.open && (openModal.type === 'flight' || openModal.type === 'campaign')}
        onClose={() => setOpenModal({ open: false, id: 0, type: 'flight' })}
        title={openModal.type === 'flight' ? 'Delete Flight' : 'Delete Campaign'}
        message={
          openModal.type === 'flight'
            ? 'Are you sure you want to delete this flight?'
            : 'Are you sure you want to delete this campaign?'
        }
        onConfirm={() =>
          openModal.type === 'flight'
            ? deleteFlightMutation(openModal.id)
            : deleteCampaignMutation()
        }
        isLoading={isPendingDeleteFlight || isPendingDeleteCampaign}
      />

      {isPendingDeleteCampaign && <CircularLoader />}
      <StackedSectionLayout
        header={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.space['24'],
              padding: theme.space['16']
            }}
          >
            <CampaignToolbar
              campaignId={campaign.id}
              onEdit={handleEdit}
              openDeleteModal={openDeleteCampaignModal}
              hasStarted={campaignHasStarted}
            />
            <Typography noWrap variant="heading-6">
              {campaign.name}
            </Typography>
            {campaignMetrics}
          </Box>
        }
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {flightsHeader}
            {campaign.flights.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.space['20']
                }}
              >
                {campaign.flights.map((flight) => (
                  <FlightAccordion
                    key={flight.id}
                    campaignId={campaign.id}
                    campaignStatus={campaign.status}
                    flight={flight}
                    refetchCampaign={refetchCampaign}
                    openDeleteModal={openDeleteFlightModal}
                    campaignContentCategory={campaign.content_category}
                    isGtvUser={isGtvUser}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: theme.space['20'],
                  backgroundColor: theme.palette.background.paper,
                  padding: theme.space['40'],
                  borderRadius: theme.borders['rounded-10']
                }}
              >
                <img src="/images/empty-flights-placeholder.svg" alt="Empty state" />
                <Typography variant="body-8">
                  Nothing here, yet. Create a flight to get started.
                </Typography>
              </Box>
            )}
          </Box>
        }
      />
    </Box>
  )
}

export default CampaignDetails
